import React, { useEffect, useMemo, useState } from 'react';
import { Globe, Check, Search, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '../ui/dropdown-menu';

/**
 * World-language switcher.
 *
 * How it works:
 *  - Google Translate is loaded (hidden) from /public/index.html.
 *  - We toggle the `googtrans` cookie (`/en/<lang>`) and reload — Google's
 *    script re-translates the whole DOM on the next paint.
 *  - This covers ALL UI strings AND dynamic CMS-driven content (hero, services,
 *    products, modals) — no per-string i18n needed.
 *  - Elements marked `.notranslate` (brand text, ₹ amounts, OTP fields) stay
 *    untouched.
 */

// 80+ languages — covers the vast majority of internet users.
// Format: { code, label (native), english }
const LANGS = [
  { code: 'en', label: 'English', en: 'English' },
  { code: 'hi', label: 'हिन्दी', en: 'Hindi' },
  { code: 'bn', label: 'বাংলা', en: 'Bengali' },
  { code: 'ta', label: 'தமிழ்', en: 'Tamil' },
  { code: 'te', label: 'తెలుగు', en: 'Telugu' },
  { code: 'mr', label: 'मराठी', en: 'Marathi' },
  { code: 'gu', label: 'ગુજરાતી', en: 'Gujarati' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ', en: 'Punjabi' },
  { code: 'kn', label: 'ಕನ್ನಡ', en: 'Kannada' },
  { code: 'ml', label: 'മലയാളം', en: 'Malayalam' },
  { code: 'or', label: 'ଓଡ଼ିଆ', en: 'Odia' },
  { code: 'ur', label: 'اردو', en: 'Urdu' },
  { code: 'ne', label: 'नेपाली', en: 'Nepali' },
  { code: 'si', label: 'සිංහල', en: 'Sinhala' },
  { code: 'zh-CN', label: '中文 (简体)', en: 'Chinese Simplified' },
  { code: 'zh-TW', label: '中文 (繁體)', en: 'Chinese Traditional' },
  { code: 'ja', label: '日本語', en: 'Japanese' },
  { code: 'ko', label: '한국어', en: 'Korean' },
  { code: 'ar', label: 'العربية', en: 'Arabic' },
  { code: 'ru', label: 'Русский', en: 'Russian' },
  { code: 'fr', label: 'Français', en: 'French' },
  { code: 'de', label: 'Deutsch', en: 'German' },
  { code: 'es', label: 'Español', en: 'Spanish' },
  { code: 'pt', label: 'Português', en: 'Portuguese' },
  { code: 'it', label: 'Italiano', en: 'Italian' },
  { code: 'nl', label: 'Nederlands', en: 'Dutch' },
  { code: 'tr', label: 'Türkçe', en: 'Turkish' },
  { code: 'vi', label: 'Tiếng Việt', en: 'Vietnamese' },
  { code: 'th', label: 'ไทย', en: 'Thai' },
  { code: 'id', label: 'Bahasa Indonesia', en: 'Indonesian' },
  { code: 'ms', label: 'Bahasa Melayu', en: 'Malay' },
  { code: 'fa', label: 'فارسی', en: 'Persian' },
  { code: 'he', label: 'עברית', en: 'Hebrew' },
  { code: 'sw', label: 'Kiswahili', en: 'Swahili' },
  { code: 'uk', label: 'Українська', en: 'Ukrainian' },
  { code: 'pl', label: 'Polski', en: 'Polish' },
  { code: 'ro', label: 'Română', en: 'Romanian' },
  { code: 'el', label: 'Ελληνικά', en: 'Greek' },
  { code: 'cs', label: 'Čeština', en: 'Czech' },
  { code: 'sv', label: 'Svenska', en: 'Swedish' },
  { code: 'da', label: 'Dansk', en: 'Danish' },
  { code: 'fi', label: 'Suomi', en: 'Finnish' },
  { code: 'no', label: 'Norsk', en: 'Norwegian' },
  { code: 'hu', label: 'Magyar', en: 'Hungarian' },
  { code: 'bg', label: 'Български', en: 'Bulgarian' },
  { code: 'sr', label: 'Српски', en: 'Serbian' },
  { code: 'sk', label: 'Slovenčina', en: 'Slovak' },
  { code: 'hr', label: 'Hrvatski', en: 'Croatian' },
  { code: 'lt', label: 'Lietuvių', en: 'Lithuanian' },
  { code: 'lv', label: 'Latviešu', en: 'Latvian' },
  { code: 'et', label: 'Eesti', en: 'Estonian' },
  { code: 'is', label: 'Íslenska', en: 'Icelandic' },
  { code: 'af', label: 'Afrikaans', en: 'Afrikaans' },
  { code: 'az', label: 'Azərbaycanca', en: 'Azerbaijani' },
  { code: 'uz', label: "O'zbekcha", en: 'Uzbek' },
  { code: 'kk', label: 'Қазақша', en: 'Kazakh' },
  { code: 'my', label: 'မြန်မာ', en: 'Burmese' },
  { code: 'km', label: 'ខ្មែរ', en: 'Khmer' },
  { code: 'lo', label: 'ລາວ', en: 'Lao' },
  { code: 'mn', label: 'Монгол', en: 'Mongolian' },
  { code: 'am', label: 'አማርኛ', en: 'Amharic' },
  { code: 'ha', label: 'Hausa', en: 'Hausa' },
  { code: 'ig', label: 'Igbo', en: 'Igbo' },
  { code: 'yo', label: 'Yorùbá', en: 'Yoruba' },
  { code: 'zu', label: 'isiZulu', en: 'Zulu' },
  { code: 'cy', label: 'Cymraeg', en: 'Welsh' },
  { code: 'ga', label: 'Gaeilge', en: 'Irish' },
  { code: 'mt', label: 'Malti', en: 'Maltese' },
  { code: 'jw', label: 'Basa Jawa', en: 'Javanese' },
  { code: 'su', label: 'Basa Sunda', en: 'Sundanese' },
  { code: 'tg', label: 'Тоҷикӣ', en: 'Tajik' },
  { code: 'tk', label: 'Türkmen', en: 'Turkmen' },
  { code: 'bs', label: 'Bosanski', en: 'Bosnian' },
  { code: 'mk', label: 'Македонски', en: 'Macedonian' },
  { code: 'sq', label: 'Shqip', en: 'Albanian' },
  { code: 'hy', label: 'Հայերեն', en: 'Armenian' },
  { code: 'ka', label: 'ქართული', en: 'Georgian' },
  { code: 'be', label: 'Беларуская', en: 'Belarusian' },
  { code: 'eo', label: 'Esperanto', en: 'Esperanto' },
  { code: 'la', label: 'Latina', en: 'Latin' },
];

const COOKIE_NAME = 'googtrans';
const STORAGE_KEY = 'preferred_lang';

function setCookie(name, value, domain) {
  const exp = new Date();
  exp.setFullYear(exp.getFullYear() + 1);
  const base = `${name}=${value};expires=${exp.toUTCString()};path=/`;
  document.cookie = base;
  if (domain) document.cookie = `${base};domain=${domain}`;
  // also set on parent domain (handy across subdomains)
  const host = window.location.hostname;
  if (host && host.indexOf('.') > 0) {
    document.cookie = `${base};domain=.${host.split('.').slice(-2).join('.')}`;
  }
}

function getCurrentLang() {
  // try cookie first
  const m = document.cookie.match(/googtrans=\/[^/]+\/([^;]+)/);
  if (m) return m[1];
  return localStorage.getItem(STORAGE_KEY) || 'en';
}

export default function LanguageSwitcher({ inDark = false, compact = false }) {
  const [cur, setCur] = useState('en');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setCur(getCurrentLang());
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return LANGS;
    return LANGS.filter(
      (l) =>
        l.code.toLowerCase().includes(q) ||
        l.label.toLowerCase().includes(q) ||
        l.en.toLowerCase().includes(q)
    );
  }, [query]);

  const change = (code) => {
    localStorage.setItem(STORAGE_KEY, code);

    // 1. Update the googtrans cookie so refreshes / first-visit pick the same lang
    if (code === 'en') {
      const expired = 'Thu, 01 Jan 1970 00:00:01 GMT';
      const host = window.location.hostname;
      document.cookie = `${COOKIE_NAME}=;expires=${expired};path=/`;
      document.cookie = `${COOKIE_NAME}=;expires=${expired};path=/;domain=${host}`;
      document.cookie = `${COOKIE_NAME}=;expires=${expired};path=/;domain=.${host}`;
    } else {
      setCookie(COOKIE_NAME, `/en/${code}`);
    }
    setCur(code);
    setOpen(false);

    // 2. Drive Google Translate's hidden select WITHOUT reload
    const trigger = () => {
      const combo = document.querySelector('.goog-te-combo');
      if (combo) {
        combo.value = code === 'en' ? '' : code;
        combo.dispatchEvent(new Event('change'));
        return true;
      }
      return false;
    };
    if (!trigger()) {
      let tries = 0;
      const id = setInterval(() => {
        tries += 1;
        if (trigger() || tries > 20) clearInterval(id);
      }, 250);
      setTimeout(() => {
        if (!document.querySelector('.goog-te-combo')) window.location.reload();
      }, 6000);
    }
  };

  const currentLang = LANGS.find((l) => l.code === cur) || LANGS[0];
  // compact = smaller trigger for tight spaces (mobile header)
  const triggerClass = compact
    ? 'notranslate h-6 sm:h-7 md:h-8 px-1.5 sm:px-2 rounded-full bg-[#302d2d] hover:bg-[#252323] border border-white/30 flex items-center justify-center gap-0.5 text-white shrink-0 transition'
    : 'notranslate h-8 md:h-8.5 px-2.5 rounded-full bg-[#302d2d] hover:bg-[#252323] border border-white/40 flex items-center justify-center gap-1 text-white hover:text-white shrink-0 transition';

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger
        className={triggerClass}
        data-testid="lang-switcher-trigger"
        aria-label="Change language"
        translate="no"
      >
        <Globe size={compact ? 12 : 15} strokeWidth={2} className="text-white" />
        <ChevronDown size={compact ? 10 : 12} className="text-white opacity-80" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="notranslate w-72 max-h-[70vh] overflow-hidden p-0 bg-zinc-950 border-zinc-800 text-zinc-100"
        translate="no"
        data-testid="lang-switcher-menu"
      >
        <div className="p-3 border-b border-zinc-800 sticky top-0 bg-zinc-950">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-2">
            Select Language
          </div>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search 80+ languages"
              className="w-full h-9 pl-8 pr-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[color:var(--gold)]"
              data-testid="lang-switcher-search"
            />
          </div>
        </div>
        <div
          className="overflow-y-auto"
          style={{ maxHeight: 'calc(70vh - 70px)' }}
        >
          {filtered.length === 0 && (
            <div className="p-6 text-center text-zinc-500 text-sm">
              No matches
            </div>
          )}
          {filtered.map((l) => {
            const on = l.code === cur;
            return (
              <button
                key={l.code}
                onClick={() => change(l.code)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-zinc-900 transition ${on ? 'bg-zinc-900/70' : ''
                  }`}
                data-testid={`lang-option-${l.code}`}
              >
                <div className="text-left min-w-0">
                  <div
                    className={`font-bold truncate ${on ? 'text-[color:var(--gold)]' : 'text-zinc-100'
                      }`}
                  >
                    {l.label}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {l.en}
                  </div>
                </div>
                {on && (
                  <Check
                    size={16}
                    className="text-[color:var(--gold)] shrink-0 ml-2"
                  />
                )}
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
